import Scrollspy from 'react-scrollspy-ez';
import styled from 'styled-components';

import BadgeStyled from '../../../components/Badge';
import ContentHeaderStyled from '../../../components/ContentHeader';
import ExternalLinkGroupStyled from '../../../components/ExternalLinkGroup';
import PaginationStyled from '../../../components/Pagination';
import { REFERENCE_LINKS } from '../../../constants';
import { IconDanger, IconSuccess } from '../../../core';
import commonPageContentStyles from '../../../styles/mixins/_commonPageContentStyles';
import navigationStyles from '../../../styles/mixins/navigationMenuStyles';

interface Props {
  className?: string;
}

const dataExternalLinkGroups = [
  {
    id: 1,
    title: 'Government usability testing resources',
    cards: [
      {
        title: 'IBM Web Accessibility Checklist',
        subtitle: '[ibm.com]',
        linkUrl: REFERENCE_LINKS.ibmAccessibilityChecklist,
      },
      {
        title: 'Government usability testing resources',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovUsabilityTesting,
      },
      {
        title: 'Testing for plain-language usability',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovPlainLanguageGuide,
      },
    ],
  },
  {
    id: 2,
    title: 'Government accessibility resources',
    cards: [
      {
        title: 'The Section 508 accessibility program',
        subtitle: '[section508.gov]',
        linkUrl: REFERENCE_LINKS.section508Program,
      },
      {
        title: 'Accessibility for Teams guide',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovAccessibilityForTeams,
      },
      {
        title: 'Section 508 Coordinators GitHub',
        subtitle: '[github.com]',
        linkUrl: REFERENCE_LINKS.section508CoordinatorsGithub,
      },
      {
        title: '18F’s accessibility guide',
        subtitle: '[guides.18f.org]',
        linkUrl: REFERENCE_LINKS.eighteenFAccessibilityGuide,
      },
      {
        title: 'Accessibility Requirements Tool (ART) for contracting',
        subtitle: '[section508.gov]',
        linkUrl: REFERENCE_LINKS.section508AccessibilityRequirementsTool,
      },
      {
        title: 'Understanding universal design',
        subtitle: '[section508.gov]',
        linkUrl: REFERENCE_LINKS.section508UniversalDesign,
      },
    ],
  },
  {
    id: 3,
    title: 'Non-government accessibility resources',
    cards: [
      {
        title: 'Web Accessibility Perspectives Videos',
        subtitle: '[w3.org]',
        linkUrl: REFERENCE_LINKS.w3cPerspectiveVideos,
      },
      {
        title: 'Inclusive design principles',
        subtitle: '[inclusivedesignprinciples.info]',
        linkUrl: REFERENCE_LINKS.inclusiveDesignPrinciples,
      },
      {
        title: 'Accessibility fundamentals with Rob Dodson',
        subtitle: '[youtube.com]',
        linkUrl: REFERENCE_LINKS.robDodsonAccessibilityFundamentals,
      },
      {
        title: 'Microsoft’s inclusive design manual',
        subtitle: '[inclusive.microsoft.design]',
        linkUrl: REFERENCE_LINKS.microsoftInclusiveDesign,
      },
      {
        title: 'Inclusive components by Heydon Pickering',
        subtitle: '[inclusive-components.design]',
        linkUrl: REFERENCE_LINKS.inclusiveComponents,
      },
      {
        title: 'Humane by design',
        subtitle: '[humanebydesign.com]',
        linkUrl: REFERENCE_LINKS.humaneByDesign,
      },
    ],
  },
];

const ContentStructure = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="content">
      <ContentHeaderStyled
        title="Content"
        breadcrumbs={[{ label: 'Get Started', url: '/' }, { label: 'Content' }]}
      />
      <div className="page-container">
        <div className="page-content">
          <div className="page-content-with-navigation-wrapper">
            <div className="page-content-with-navigation">
              <div className="page-content-item">
                <h2 className="h1" id="overview">
                  Overview
                </h2>
                <h3 className="h3" id="definition">
                  Definition
                </h3>
                <p className="mb-40">
                  The Content Writing Page serves as the heart of our digital experience, where
                  information is organized, presented, and made accessible to users. It is designed
                  to provide clear, relevant, and engaging content in a structured format that
                  enhances user comprehension and interaction. Our content pages prioritize
                  readability, consistency, and easy navigation, ensuring that all users, regardless
                  of their abilities, can access and understand the information presented. By
                  aligning with accessibility best practices, we make sure that every content page
                  meets the needs of a diverse audience, offering a seamless experience across
                  devices and platforms.
                </p>
                <h3 className="h3" id="rationale">
                  Rationale
                </h3>
                <p className="mb-40">
                  The rationale behind the Content Writing Page is to ensure that all content we
                  create is clear, accessible, and user-centered. By establishing guidelines for
                  writing that prioritize clarity, consistency, and inclusivity, we aim to enhance
                  user comprehension and engagement. This approach ensures that every piece of
                  content is not only easy to read but also accessible to people with diverse
                  abilities, promoting a more inclusive digital experience. The goal is to create
                  content that works seamlessly for all users, regardless of their needs or the
                  technology they use to access it.
                </p>
              </div>
              <h2 className="divider h1" id="guidelines">
                Guidelines
              </h2>
              <div className="page-content-item">
                <h3 className="h3" id="best-practices">
                  Best Practices
                </h3>
                <ul className="ordered-list">
                  <li>
                    <strong>Organizing Content for Easy Navigation:</strong> Structure content with
                    clear, descriptive headings and subheadings to help users easily find
                    information. This ensures a smooth experience, especially for those using
                    assistive technologies like screen readers.
                  </li>
                  <li>
                    <strong>Ensuring Readability and Accessibility:</strong> Use simple language,
                    proper contrast, and scalable text. Avoid long blocks of text, and ensure font
                    sizes are adjustable for an optimal experience across devices.
                  </li>
                  <li>
                    <strong>Providing Text Alternatives for Inclusivity:</strong> Always include alt
                    text for images, videos, and other media, along with transcripts and captions.
                    This ensures content is accessible to users with visual or hearing impairments.
                  </li>
                </ul>
              </div>
              <figure>
                <img
                  src="/images/get-started-content/use-sentence-case-capitalization@3x.jpg"
                  alt="Use sentence—case capitalization"
                />
                <figcaption>Use sentence—case capitalization</figcaption>
              </figure>
              <div className="page-content-item mb-40">
                <div className="visually-hidden" aria-hidden="true" id="capitalization">
                  Capitalization
                </div>
                <h3 className="h3">Do not use title case capitalization</h3>
                <p>
                  Title (or headline) case capitalization is where the first letter of most words is
                  capitalized, but this style can hinder readability and slow down scanning.
                  Instead, using sentence case—where only the first word and proper nouns are
                  capitalized—creates a more natural reading flow. Sentence case improves
                  comprehension, enhances accessibility for users with cognitive impairments, and
                  ensures a consistent, user-friendly experience. Adopting sentence case across
                  headings and content helps maintain clarity and reduces visual strain for all
                  users.
                </p>
              </div>
              <div className="ex-cards">
                <div className="ex-card true">
                  <img
                    className="ex-card-img"
                    src="/images/get-started-content/screen-not-title-case-capitalization@3x.jpg"
                    alt="Only capitalize proper nouns, such as product names."
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                      Do
                    </BadgeStyled>
                    <p>Only capitalize proper nouns, such as product names.</p>
                  </div>
                </div>
                <div className="ex-card false">
                  <img
                    className="ex-card-img"
                    src="/images/get-started-content/screen-title-case-capitalization@3x.jpg"
                    alt="Only capitalize proper nouns, such as product names."
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                      Don&apos;t
                    </BadgeStyled>
                    <p>Only capitalize proper nouns, such as product names.</p>
                  </div>
                </div>
              </div>
              <div className="page-content-item mb-40">
                <h3 className="h3" id="principles">
                  Do not use all caps capitalization
                </h3>
                <p>
                  All caps (or uppercase) capitalization is where every letter in a word is
                  capitalized, which can hinder readability and comprehension. Text written in all
                  caps is more difficult to scan and can create visual fatigue for users. It may
                  also come across as aggressive or overwhelming, especially for those with
                  cognitive or visual impairments. Instead, use sentence case or title case for a
                  clearer, more accessible reading experience. This ensures that content is easy to
                  read and provides a more user-friendly interface for everyone.
                </p>
              </div>
              <div className="ex-cards">
                <div className="ex-card true">
                  <img
                    className="ex-card-img"
                    src="/images/get-started-content/screen-not-use-all-caps-capitalization@3x.jpg"
                    alt="Only capitalize proper nouns, such as product names"
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                      Do
                    </BadgeStyled>
                    <p>Only capitalize proper nouns, such as product names.</p>
                  </div>
                </div>
                <div className="ex-card false">
                  <img
                    className="ex-card-img"
                    src="/images/get-started-content/screen-use-all-caps-capitalization@3x.jpg"
                    alt="Only capitalize proper nouns, such as product names"
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                      Don&apos;t
                    </BadgeStyled>
                    <p>Only capitalize proper nouns, such as product names.</p>
                  </div>
                </div>
                <div className="ex-card false">
                  <img
                    className="ex-card-img"
                    src="/images/get-started-content/screen-error-toast@3x.jpg"
                    alt="Toast messages"
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                      Don&apos;t
                    </BadgeStyled>
                    <p>
                      Although error toast is still available, we discourage its use. Toast messages
                      are too short to adequately explain what went wrong and how to fix the
                      problem. Because the toast component appears at the bottom of the screen and
                      disappears after 3 seconds.
                    </p>
                  </div>
                </div>
                <div className="ex-card false">
                  <img
                    className="ex-card-img"
                    src="/images/get-started-content/screen-modal-dialogs@3x.jpg"
                    alt="Modal dialogs"
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                      Don&apos;t
                    </BadgeStyled>
                    <p>
                      Modal dialogs are a good way to ask to confirm a destructive action but not to
                      tell them an error has occurred. Modals block users until a decision is made,
                      which is likely to make users feel pressured.
                    </p>
                  </div>
                </div>
              </div>
              <div className="page-content-item">
                <h3 className="h3" id="considerations">
                  Considerations
                </h3>
                <ul className="ordered-list">
                  <li>
                    <strong>Diverse Abilities:</strong> Ensure content is accessible to users with
                    varying abilities, including those with visual impairments, hearing
                    disabilities, or cognitive challenges, by providing alternative text, clear
                    language, and navigational support.
                  </li>
                  <li>
                    <strong>Mobile Accessibility:</strong> Optimize content for mobile devices,
                    ensuring that it remains readable and user-friendly on smaller screens with
                    adjustable text sizes and responsive design.
                  </li>
                  <li>
                    <strong>Content Organization:</strong> Structure content logically, using
                    headings, subheadings, and concise paragraphs to make it easy to navigate,
                    especially for users relying on screen readers or scanning for key information.
                  </li>
                </ul>
                <h3 className="h3" id="actionable-language">
                  Actionable language
                </h3>
                <p>
                  Think of headings and subheadings as signposts that guide users through the
                  content. To make these guides clear and effective, follow these principles:
                </p>
                <ul className="disc-list">
                  <li>
                    Keep headings concise—one sentence is all it takes to convey the main idea.
                  </li>
                  <li>Choose simple, direct language that immediately communicates the point.</li>
                  <li>
                    Leave out punctuation like periods, commas, or semicolons—let the heading stand
                    on its own.
                  </li>
                  <li>
                    Opt for sentence case, capitalizing only the first word and proper nouns for a
                    cleaner look.
                  </li>
                </ul>
              </div>
              <div className="reccomendations mb-40">
                <div className="reccomendation-item">
                  <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                    Do
                  </BadgeStyled>
                  <ul className="disc-list">
                    <li>Online store dashboard</li>
                    <li>Custom reports</li>
                    <li>Sell your products in person</li>
                  </ul>
                </div>
                <div className="reccomendation-item">
                  <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                    Don&apos;t
                  </BadgeStyled>
                  <ul className="disc-list">
                    <li>This is your online store dashboard</li>
                    <li>Custom Reports</li>
                    <li>Sell your products in person!</li>
                  </ul>
                </div>
                <div className="reccomendation-item">
                  <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                    Do
                  </BadgeStyled>
                  <ul className="disc-list">
                    <li>Secure your account with two-step authentication</li>
                  </ul>
                </div>
                <div className="reccomendation-item">
                  <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                    Don&apos;t
                  </BadgeStyled>
                  <ul className="disc-list">
                    <li>Two-step authentication</li>
                  </ul>
                </div>
                <div className="reccomendation-item">
                  <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                    Do
                  </BadgeStyled>
                  <ul className="disc-list">
                    <li>Activate Apple pay</li>
                    <li>Explore free themes</li>
                    <li>View shipping settings</li>
                  </ul>
                </div>
                <div className="reccomendation-item">
                  <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                    Don&apos;t
                  </BadgeStyled>
                  <ul className="disc-list">
                    <li>Try Apple Pay</li>
                    <li>Free themes</li>
                    <li>Settings</li>
                  </ul>
                </div>
              </div>
              <h2 className="h1 divider" id="references">
                References
              </h2>
              {dataExternalLinkGroups.map((externalLinkGroup) => (
                <ExternalLinkGroupStyled
                  key={externalLinkGroup.id}
                  title={externalLinkGroup.title}
                  cards={externalLinkGroup.cards}
                />
              ))}
              <div className="page-content-item">
                <p>
                  By following these content writing guidelines and considerations, we can create
                  digital products that are accessible, inclusive, and easy to navigate, ensuring a
                  seamless experience for all users.
                </p>
              </div>
            </div>
            <div>
              <div className="page-navigation">
                <h2>Contents</h2>
                <Scrollspy
                  ids={[
                    'overview',
                    'definition',
                    'rationale',
                    'guidelines',
                    'best-practices',
                    'capitalization',
                    'considerations',
                    'actionable-language',
                    'references',
                  ]}
                  offset={0}
                  activeItemClassName="active"
                  itemContainerClassName="multilevel-link-list"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <PaginationStyled
        previousLink="/get-started/accessibility"
        previous="Accessibility"
        nextLink="/get-started/voice-and-tone"
        next="Voice & Tone"
      />
    </div>
  </div>
);

const Content = styled(ContentStructure)`
  ${commonPageContentStyles}
  ${navigationStyles([2, 3, 5, 6, 7, 8])}

    overflow: unset;

  height: 100%;

  > .content {
    width: 100%;
    padding: 0;
  }
`;

export default Content;
